import { API_LOGOUT } from '../constants/api';
import request from './api';

/** Pantallas a las que se devuelve al usuario tras cerrar sesión */
export const LOGIN_PATH = '/login';
export const LOGIN_WIDGET_PATH = '/login_widget';

/** Ruta guardada antes de mandar al usuario al login, para volver luego */
export const REDIRECT_PATH_KEY = 'msalRedirectPath';

/**
 * Elimina del navegador lo que guarda la propia intranet.
 *
 * La caché de MSAL no se toca aquí: la vacía `logoutRedirect()` con su propio
 * esquema de claves. La cookie de sesión tampoco, porque es HttpOnly y solo
 * desaparece con el Set-Cookie caducado que devuelve el servicio de logout.
 */
export const clearClientStorage = () => {
  try {
    window.sessionStorage.removeItem(REDIRECT_PATH_KEY);
  } catch (error) {
    // Storage no disponible (navegación privada o cookies bloqueadas)
  }
};

/**
 * Cierra la sesión de la intranet: el backend la invalida en servidor y caduca
 * la cookie.
 *
 * Va por XHR (request ya envía withCredentials) en lugar de encadenarse al
 * redirect de Azure, para que ocurra siempre: el cierre es efectivo aunque el
 * usuario se quede en la pantalla de Microsoft o cierre la pestaña.
 *
 * Nunca rechaza: el cierre de sesión debe continuar aunque el servicio falle.
 * @returns {Promise}
 */
export const closeSession = () =>
  request(API_LOGOUT, { method: 'POST' }, false)
    .catch(error => {
      // Sin esta traza el fallo es invisible: el usuario ve el mismo logout
      // "correcto" aunque su sesión siga viva en el backend.
      console.error('No se pudo cerrar la sesión en el backend', error);
      return null;
    })
    .then(clearClientStorage);

/**
 * Cierra la sesión del usuario de principio a fin. Único punto de entrada al
 * logout: cualquier pantalla que lo ofrezca debe llamar aquí.
 *
 * El orden importa. Primero la intranet, que es la que protege los datos: si
 * el proceso se rompe a mitad, el usuario queda fuera de la aplicación aunque
 * siga identificado en Azure, que es el lado seguro del fallo.
 *
 * @param {Object} params
 * @param {Object} [params.instance] - Instancia de MSAL del MsalProvider.
 * @param {string} [params.loginPath] - Pantalla de login a la que volver.
 * @param {boolean} [params.closeIdentityProvider] - false limpia la caché de
 * MSAL pero mantiene viva la sesión SSO de Azure, sin salir de la aplicación.
 * @returns {Promise}
 */
export const logout = async ({
  instance = null,
  loginPath = LOGIN_PATH,
  closeIdentityProvider = true
} = {}) => {
  await closeSession();

  if (!instance) {
    window.location.replace(loginPath);
    return;
  }

  try {
    if (closeIdentityProvider) {
      // Pasar la cuenta hace que viaje el logout_hint; sin él Azure muestra el
      // selector de cuentas en lugar de cerrar la sesión directamente.
      const account =
        instance.getActiveAccount() || instance.getAllAccounts()[0];

      // Navega fuera de la aplicación, así que nada posterior se ejecuta.
      await instance.logoutRedirect({ account });
      return;
    }

    // onRedirectNavigate => false aborta la navegación a Azure, pero la caché
    // ya se ha vaciado para entonces: es la forma que da MSAL de limpiar sus
    // credenciales sin sacar al usuario de la aplicación.
    await instance.logoutRedirect({ onRedirectNavigate: () => false });
  } catch (error) {
    console.error('No se pudo cerrar la sesión de Azure', error);
  }

  window.location.replace(loginPath);
};
